import { businessDayOf } from '@app/shared/time';
import { normalizeSearch } from '@app/hr/hr-leave-board';
import type { RentalInterruption } from '@app/shared/_models/sales';
import type { Day, DayState, RentalLedgerEntry, RentalLifecycleFull, RentalLineView } from '../rental-billing.model';
import { DAY_STATES } from '../rental-billing.model';
import {
  CREDIT_KIND_LABELS, DAY_STATE_LABELS, INTERRUPTED_BADGE, INTERRUPTED_KIND_LABELS, OUTSIDE_PRESENT_LABEL, closureTip, dayStateClass, docPatients, docTypeLabel, lineClosed, patientLabel, rentalSubtitle, rentalTitle, showInterruptedBadge, statusClass, statusLabel,
} from '../rental-billing.labels';
import { addDays, frDay, frShort, frWeekday, minDay, money } from '../rental-days';
import {
  BlArrow, BlRef, CREDITABLE_STATES, CreditKind, DayWindow, GroupedDecision, InterruptedKind, Marker, MemoGroup, StateSegment, blArrowsOf, carriedNotesOf, creditAt, creditPiecesAt, dayPct, dayTicksOfWindow, deliveredDay, dueAt, groupDecisionsOf, interruptedOn,
  interruptionsAt, ledgerAt, ledgerBarsOf, lineBucket, markersOf, memoGroupsOf, monthGridCssOfWindow, monthLabelFor,
  monthTicksOfWindow, outsidePresentOf, periodTicksOf, placeRibbons, prescriptionBarsOf, presenceBarOf, presenceRange, pxPerDay, returnedDay,
  rankSpanAt, ribbonLabel, splitCredited, shownStateAt, splitInterrupted, stateSegmentsOf, weekendGridCss, windowDays, interruptedKindAt, creditKindAt,
} from '../rental-planning.geometry';

// Modèle d'affichage du Planning : positions et textes tirés des réponses du serveur, aucun état de facturation calculé ici.
// Toutes les plages lues sont start / end, premier et dernier jour inclus ; un geste commercial est une interruption au motif offert (reasonOffered).
export interface PlanningNames {
  reasonName(id: number): string;
  reasonColor(id: number): string | null;
  reasonOffered(id: number): boolean; // motif c_values 42 aux jours offerts (data.offered = true) : rendu violet et « Offert » du geste
  userName(id: number): string;
  periodName(id: number | null): string | null;
  beneficiary?: string | null; // patient des lignes sans mémo (store.beneficiary)
}

export type Bucket = 'current' | 'returned' | 'all';
export interface OwnFilters { bucket: Bucket; hideClosed: boolean; }
export interface PartyRef { id: number; name: string; }
// customerId : client de la pièce, pour ouvrir sa fiche depuis le nom du dialogue (le nom vient de la vue du moteur).
export interface PlanningParties { customerId?: number | null; beneficiary: PartyRef | null; payers: PartyRef[]; }
// Ligne d'en-tête : texte, puis noms de tiers cliquables (fiche en fenêtre), puis suite.
export interface HeadLine { text: string; parties: PartyRef[]; post?: string; }

export interface Span { left: number; width: number; }
export interface SegVM extends Span { cls: string; state: DayState; kind?: InterruptedKind; }
export interface MarkerVM { kind: Marker['kind']; left: number; title: string; }
export interface BlArrowVM { id: number; kind: BlArrow['kind']; done: boolean; left: number; cls: string; aria: string; }
export interface LedgerBarVM extends Span { credit: boolean; }
export interface MetaPart { text: string; b?: boolean; }
export interface PillVM { cls: string; label: string; title?: string; }

export interface LineRowVM {
  id: number; seq: number; name: string; product: string | null; meta: MetaPart[]; pills: PillVM[]; // name = intitulé du contrat, product = l'article dessous
  contract: { path: (string | number)[]; title: string } | null; // fiche du contrat de location d'origine ; null sans contrat lisible
  article: { path: (string | number)[]; title: string } | null; // fiche produit de l'article sous l'intitulé ; null sans contrat (le titre est l'article)
  anomalies: string[]; blocked: boolean; unreadable: boolean; actionable: boolean;
  presence: (Span & { fut: boolean })[]; prescriptions: Span[]; markers: MarkerVM[]; arrows: BlArrowVM[];
  segs: SegVM[]; outside: Span[]; ledger: LedgerBarVM[]; ticks: number[];
}

export interface RibbonVM extends Span {
  key: string; d: GroupedDecision; label: string; top: number; color: string | null; offer: boolean; partial: boolean; scheduled: boolean;
  open: boolean; cancelled: boolean; edited: boolean; anchorRight: boolean; missing: boolean; aria: string; missingAria: string;
}

export interface GroupVM { key: string; title: string; memo: string | null; count: string; lines: LineRowVM[]; readableIds: number[]; ribbons: RibbonVM[]; height: number; }
export interface MonthVM { first: Day; label: string; title: string; left: number; width: number; }
export interface DayTickVM { day: Day; label: string; left: number; today: boolean; }
export interface LegendItem { cls: string; label: string; kind: 'state' | 'presence' | 'rx' | 'piece' | 'today' | 'marker' | 'arrow'; marker?: Marker['kind']; title?: string; }
export interface SummaryItem { pre: string; b?: string; post?: string; cls?: string; title?: string; }

export interface PlanningVM {
  n: number; months: MonthVM[]; days: DayTickVM[]; bgImage: string; bgSize: string; todayPct: number | null;
  groups: GroupVM[]; legend: LegendItem[]; empty: boolean; memoCount: number;
}

export const MARKER_LABELS: Record<Marker['kind'], string> = { start: 'Début corrigé', end: 'Fin décidée' };
const MARKER_ORDER: Marker['kind'][] = ['start', 'end'];
// Flèches des BL : verte ▶ livraison, rouge ◀ reprise ; pointillée prévue, pleine signée (JP 30/10/2026).
const ARROWS: { kind: BlArrow['kind']; done: boolean; label: string }[] = [
  { kind: 'delivery', done: false, label: 'Livraison prévue' }, { kind: 'delivery', done: true, label: 'Livraison réalisée' },
  { kind: 'return', done: false, label: 'Reprise prévue' }, { kind: 'return', done: true, label: 'Reprise réalisée' },
];
export function arrowLabel(kind: BlArrow['kind'], done: boolean): string {
  return ARROWS.find((a) => a.kind === kind && a.done === done)!.label;
}
export function arrowCls(kind: BlArrow['kind'], done: boolean): string {
  return 'bl-arrow ' + (kind === 'return' ? 'ret' : 'del') + (done ? ' done' : ' plan');
}
const PIECE_STATES: ReadonlySet<DayState> = new Set<DayState>(['BILLED', 'BILLED_EARLY', 'PAID_INTERRUPTED', 'COMPENSATED']);

export function isPieceState(s: DayState | null): boolean {
  return s !== null && PIECE_STATES.has(s);
}

// Ligne qui peut porter une décision : enregistrée, lisible et non close (2107).
export function readableLine(l: RentalLineView): boolean {
  return l.id > 0 && !l.lifecycle_unreadable && l.decisions != null && !lineClosed(l);
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

function dayOfInstant(at: string | null | undefined): string {
  return frShort(businessDayOf(at ?? null));
}

// Lignes retenues : celles de la page si elle filtre, sinon les filtres propres du Planning.
export function visibleLinesOf(v: RentalLifecycleFull, visibleLineIds: number[] | null, f: OwnFilters): RentalLineView[] {
  const rental = v.lines.filter((l) => l.typ === 'RENTAL');
  if (visibleLineIds) {
    const keep = new Set(visibleLineIds);
    return rental.filter((l) => keep.has(l.id));
  }
  return rental.filter((l) => {
    if (f.hideClosed && l.status === 'closed') return false;
    const b = lineBucket(l);
    return f.bucket === 'all' || (f.bucket === 'returned') === (b === 'returned');
  });
}

export function bucketCounts(v: RentalLifecycleFull, hideClosed: boolean): Record<Bucket, number> {
  const rental = v.lines.filter((l) => l.typ === 'RENTAL' && !(hideClosed && l.status === 'closed'));
  const returned = rental.filter((l) => lineBucket(l) === 'returned').length;
  return { current: rental.length - returned, returned, all: rental.length };
}

export function lineRowOf(l: RentalLineView, v: RentalLifecycleFull, w: DayWindow, names: PlanningNames, bls: readonly BlRef[] = []): LineRowVM {
  const asOf = v.as_of;
  const unreadable = l.lifecycle_unreadable || l.decisions == null;
  const segs: SegVM[] = stateSegmentsOf(l.days ?? [], w, asOf, !!returnedDay(l))
    .flatMap((s): (StateSegment & { kind?: InterruptedKind })[] => (s.state === 'INTERRUPTED' ? splitInterrupted(l, s, w) : CREDITABLE_STATES.has(s.state) ? splitCredited(l, s, w) : [s]))
    .map((s) => ({
      left: s.left, width: s.width, state: s.state, kind: s.kind,
      cls: (s.kind ? (s.state === 'INTERRUPTED' ? 's-int-' : 's-') + s.kind : dayStateClass(s.state)) + (s.clippedStart ? '' : ' rs') + (s.clippedEnd ? '' : ' re') + (s.fut ? ' fut' : ''),
    }));
  const presence: (Span & { fut: boolean })[] = [];
  const p = presenceBarOf(l, w, asOf);
  if (p) {
    const cut = p.open ? Math.min(Math.max(dayPct(asOf, w), p.left), p.left + p.width) : p.left + p.width; // présence ouverte : estompée dès le trait rouge
    if (cut > p.left) presence.push({ left: p.left, width: cut - p.left, fut: false });
    if (p.left + p.width > cut) presence.push({ left: cut, width: p.left + p.width - cut, fut: true });
  }
  const half = 50 / windowDays(w);
  // Le repère de fin est dessiné sur le premier jour non facturé, lendemain de window_end (0.2) : la pastille et lui disent le même dernier jour facturé.
  const markers = markersOf(l, w, v.lines).map((m) => ({
    kind: m.kind, left: m.left + half,
    title: m.kind === 'end' ? `${MARKER_LABELS.end} : dernier jour facturé le ${frShort(addDays(m.day, -1))}` : `${MARKER_LABELS[m.kind]} le ${frShort(m.day)}`,
  }));
  const arrows = blArrowsOf(bls, w).map((a) => ({
    id: a.id, kind: a.kind, done: a.done, left: a.left, cls: arrowCls(a.kind, a.done), aria: `${arrowLabel(a.kind, a.done)} le ${frShort(a.day)}, BL N°${a.id}`,
  }));
  const price = l.due?.[0]?.uprice_taxx ?? l.ledger?.[l.ledger.length - 1]?.uprice_taxx ?? null;
  const period = names.periodName(l.period);
  const head = [period, price !== null ? `${money(price)} HT` : null].filter((x): x is string => !!x).join(' · ');
  const meta: MetaPart[] = head ? [{ text: head + ' · ' }] : [];
  // Livré / repris avec l'année, dans l'ordre du Tableau : une ligne close d'un an d'historique doit dire quand elle a été louée.
  const delivered = deliveredDay(l);
  if (delivered) meta.push({ text: `livré le ${frDay(delivered)} · ` });
  if (l.return_realdatdel) meta.push({ text: `repris le ${frDay(returnedDay(l))} · ` });
  if (l.billed_until) meta.push({ text: 'facturé jusqu\'au ' }, { text: frShort(l.billed_until), b: true });
  else meta.push({ text: 'rien de facturé', b: true });
  meta.push({ text: ` · rang ${l.rank}/${l.duration && l.duration > 0 ? l.duration : '∞'}` });
  const pills: PillVM[] = [{ cls: 'pill ' + statusClass(l.status), label: statusLabel(l.status) }];
  if (showInterruptedBadge(l, asOf)) pills.push({ cls: 'pill st-interrupted', label: INTERRUPTED_BADGE });
  const s = l.summary;
  if (s?.window_start && delivered && s.window_start < delivered) pills.push({ cls: 'pill start-fixed', label: 'début corrigé', title: `Facturation à partir du ${frShort(s.window_start)}, livré le ${frShort(delivered)}` });
  if (l.decisions?.effective_end_id != null && s?.window_end) pills.push({ cls: 'pill end-fixed', label: `fin décidée le ${frShort(s.window_end)}` });
  if (lineClosed(l)) pills.push({ cls: 'pill st-closure', label: 'close', title: closureTip(l.closure!) });
  const article = rentalSubtitle(l);
  return {
    id: l.id, seq: l.seq, name: rentalTitle(l) ?? `Article ${l.product}`, product: article, meta, pills,
    contract: l.terms_id != null
      ? { path: ['/sales/rental-product', l.product, l.terms_id], title: `Ouvrir le contrat de location « ${l.terms_name?.trim() || 'n° ' + l.terms_id} »` }
      : null,
    article: article ? { path: ['/sales/product', l.product], title: `Ouvrir l'article « ${article} »` } : null,
    anomalies: l.anomalies ?? [], blocked: l.blocked || unreadable, unreadable, actionable: readableLine(l),
    presence, prescriptions: prescriptionBarsOf(l, w).map((b) => ({ left: b.left, width: b.width })), markers, arrows, segs,
    outside: outsidePresentOf(l, w).map((b) => ({ left: b.left, width: b.width })),
    ledger: ledgerBarsOf(l, w).map((b) => ({ left: b.left, width: b.width, credit: b.credit })),
    ticks: periodTicksOf(l, w, v.current_day_due),
  };
}

export function groupOf(g: MemoGroup, visible: RentalLineView[], v: RentalLifecycleFull, w: DayWindow, ppd: number, names: PlanningNames, blsOf: (lineId: number) => readonly BlRef[] = () => []): GroupVM {
  const byId = new Map(g.lines.map((l) => [l.id, l]));
  const decisions = groupDecisionsOf(g);
  const labels = new Map<string, string>();
  const inputs = decisions.map((d) => {
    const carriers = [...new Set(d.entries.map((e) => e.lineId))].map((id) => byId.get(id)).filter((l): l is RentalLineView => !!l);
    const label = ribbonLabel(d, names.reasonName(d.sample.reason_id), d.partial ? carriers.map((l) => l.product_name ?? `Article ${l.product}`) : null);
    labels.set(d.key, label);
    // Le ⚠ du §7.6 suit le libellé : il compte dans l'étendue réservée.
    return { key: d.key, start: d.sample.start, end: d.sample.end, label: d.missing.length ? label + '  ⚠' : label, floor: minDay(...carriers.map(deliveredDay)) };
  });
  const placed = placeRibbons(inputs, w, v.as_of, ppd);
  const byKey = new Map(decisions.map((d) => [d.key, d]));
  const ribbons: RibbonVM[] = placed.ribbons.map((r) => {
    const d = byKey.get(r.key)!;
    const offer = names.reasonOffered(d.sample.reason_id);
    const color = offer ? null : names.reasonColor(d.sample.reason_id); // les gestes (motif offert) restent violets, comme avant
    return {
      key: r.key, d, label: labels.get(r.key) ?? '', left: r.left, width: r.width, top: 5 + r.lane * 24, color, offer,
      partial: d.partial, scheduled: r.scheduled, open: r.open, cancelled: d.cancelled, edited: d.edited, anchorRight: r.anchorRight,
      missing: d.missing.length > 0 && !d.cancelled,
      aria: `${!d.cancelled ? 'Modifier' : 'Détail'} : ${labels.get(r.key) ?? ''}`,
      missingAria: d.missing.length && !d.cancelled ? `Appliquer à ce matériel : ${missingTip(d, g.lines).join(' ; ')}` : '',
    };
  });
  const readableIds = visible.filter(readableLine).map((l) => l.id);
  return {
    key: g.key, title: patientLabel(g.memo, names.beneficiary), memo: g.memo, count: plural(g.lines.length, 'matériel', 'matériels'),
    lines: visible.map((l) => lineRowOf(l, v, w, names, blsOf(l.id))), readableIds, ribbons, height: Math.max(placed.lanes, 1) * 24 + 10,
  };
}

// blsOf : BL du dossier par ligne, lus avec les pièces (o=deliveries) ; avant leur lecture, aucune flèche.
export function buildPlanning(v: RentalLifecycleFull, w: DayWindow, trackPx: number, visible: RentalLineView[], search: string, names: PlanningNames, blsOf: (lineId: number) => readonly BlRef[] = () => []): PlanningVM {
  const ppd = pxPerDay(Math.max(trackPx, 1), w);
  const keep = new Set(visible.map((l) => l.id));
  const memoGroups = memoGroupsOf(v.lines);
  const q = memoGroups.length >= 2 ? normalizeSearch(search) : ''; // recherche proposée dès 2 mémos
  const groups: GroupVM[] = [];
  for (const g of memoGroups) {
    const vis = g.lines.filter((l) => keep.has(l.id));
    if (!vis.length || (q && !normalizeSearch(g.memo ?? names.beneficiary).includes(q))) continue; // sans mémo, on cherche le bénéficiaire affiché
    groups.push(groupOf(g, vis, v, w, ppd, names, blsOf));
  }
  const grid = weekendGridCss(w);
  const asOfIn = v.as_of >= w.start && v.as_of <= w.end;
  const weekends = ppd >= 6; // en dessous, les week-ends ne font plus qu'un hachage dense : seul le quadrillage des mois reste
  return {
    n: windowDays(w),
    months: monthTicksOfWindow(w).map((t) => ({ first: t.first, label: monthLabelFor(t, trackPx), title: t.label, left: t.left, width: t.width })),
    days: spacedTicks(dayTicksOfWindow(w, v.as_of, ppd), trackPx),
    bgImage: weekends ? `${monthGridCssOfWindow(w)}, ${grid.image}` : monthGridCssOfWindow(w),
    bgSize: weekends ? `100% 100%, ${grid.size}` : '100% 100%',
    todayPct: asOfIn ? dayPct(v.as_of, w) : null, // début d'as_of, comme son numéro : ce qui arrive aujourd'hui (livraison, interruption) part du trait (JP 02/11)
    groups, legend: legendOf(groups, asOfIn), empty: groups.length === 0, memoCount: memoGroups.length,
  };
}

// Numéros de jour sans chevauchement (chiffres de 10 px ≈ 5,6 px), tous au début de leur colonne, aujourd'hui compris (régulièrement espacés) ;
// celui d'aujourd'hui reste toujours, quitte à chasser le voisin qu'il recouvrirait.
export function spacedTicks(ticks: DayTickVM[], trackPx: number): DayTickVM[] {
  const kept: DayTickVM[] = [];
  const w = (t: DayTickVM) => t.label.length * 5.6 + 2;
  const fits = (a: DayTickVM, b: DayTickVM) => ((b.left - a.left) / 100) * trackPx >= w(a);
  for (const t of ticks) {
    if (t.today) {
      while (kept.length && !fits(kept[kept.length - 1], t)) kept.pop();
      kept.push(t);
    } else if (!kept.length || fits(kept[kept.length - 1], t)) {
      kept.push(t);
    }
  }
  return kept;
}

// Seuls les états et repères présents dans la fenêtre, dans l'ordre des états du serveur ; une interruption visible montre ses trois natures.
export function legendOf(groups: GroupVM[], asOfIn: boolean): LegendItem[] {
  const states = new Set<DayState>();
  const credits = new Set<CreditKind>();
  const markers = new Set<Marker['kind']>();
  const arrows = new Set<string>();
  let outside = false, presence = false, rx = false, inv = false, cred = false, ticks = false;
  for (const g of groups) for (const r of g.lines) {
    r.segs.forEach((s) => (s.kind && s.state !== 'INTERRUPTED' ? credits.add(s.kind as CreditKind) : states.add(s.state)));
    r.markers.forEach((m) => markers.add(m.kind));
    r.arrows.forEach((a) => arrows.add(a.cls));
    outside ||= r.outside.length > 0;
    presence ||= r.presence.length > 0;
    rx ||= r.prescriptions.length > 0;
    inv ||= r.ledger.some((b) => !b.credit);
    cred ||= r.ledger.some((b) => b.credit);
    ticks ||= r.ticks.length > 0;
  }
  const out: LegendItem[] = DAY_STATES.filter((s) => states.has(s)).flatMap((s) => s === 'INTERRUPTED'
    ? (Object.keys(INTERRUPTED_KIND_LABELS) as InterruptedKind[]).map((k) => ({ cls: 'sw s-int-' + k, label: INTERRUPTED_KIND_LABELS[k], kind: 'state' as const }))
    : [{ cls: 'sw ' + dayStateClass(s), label: DAY_STATE_LABELS[s], kind: 'state' as const }]);
  for (const k of ['credit_due', 'credited'] as CreditKind[]) if (credits.has(k)) out.push({ cls: 'sw s-' + k, label: CREDIT_KIND_LABELS[k], kind: 'state' });
  if (outside) out.push({ cls: 'sw s-outside-present', label: 'Non facturé (matériel en place)', kind: 'state' });
  if (presence) out.push({ cls: 'sw lg-presence', label: 'Présence (livré → repris)', kind: 'presence' });
  if (rx) out.push({ cls: 'sw lg-rx', label: 'Prescription', kind: 'rx' });
  // Piste sous la bande (JP 30/10) : la bande dit l'état de chaque jour, le trait la pièce émise qui couvre ces jours.
  if (inv) out.push({ cls: 'sw lg-inv', label: 'Facture émise', kind: 'piece', title: 'Trait sous la bande : une facture émise, du premier au dernier jour qu\'elle couvre. La bande dit l\'état de chaque jour, le trait la pièce qui l\'a facturé.' });
  if (cred) out.push({ cls: 'sw lg-cred', label: 'Avoir émis', kind: 'piece', title: 'Trait rouge sous la bande : un avoir émis, du premier au dernier jour qu\'il rend.' });
  if (ticks) out.push({ cls: 'lg-tick', label: 'Début de période', kind: 'piece', title: 'Petit trait : premier jour de chaque période facturée ou due (semaine, mois…), et fin de la période due à la prochaine échéance.' });
  if (asOfIn) out.push({ cls: 'sw lg-today', label: 'Aujourd\'hui', kind: 'today' });
  for (const a of ARROWS) if (arrows.has(arrowCls(a.kind, a.done))) out.push({ cls: arrowCls(a.kind, a.done), label: a.label, kind: 'arrow' });
  for (const k of MARKER_ORDER) if (markers.has(k)) out.push({ cls: 'lg-mk', label: MARKER_LABELS[k], kind: 'marker', marker: k });
  return out;
}

export function summaryOf(v: RentalLifecycleFull, names: PlanningNames, openOnly = false): SummaryItem[] {
  const out: SummaryItem[] = [];
  const rental = v.lines.filter((l) => l.typ === 'RENTAL');
  const billed = new Set(rental.map((l) => l.billed_until ?? ''));
  if (billed.size === 1) {
    const d = [...billed][0];
    out.push(d ? { pre: 'Facturé jusqu\'au ', b: frShort(d), post: ' inclus' } : { pre: 'Rien de facturé' });
  } else if (billed.size > 1) {
    out.push({ pre: 'Facturé jusqu\'au : voir chaque matériel' });
  }
  const a = v.amounts;
  if (a?.refused) {
    out.push({ pre: '', b: 'Passage bloqué', post: ' : ' + a.refused, cls: 'err' });
  } else if (a) {
    // Vue « open » : les matériels repris ne sont ni lus ni chiffrés, le montant ne couvre que ceux en place.
    const scope = openOnly ? ' HT (matériels en place)' : ' HT';
    out.push({ pre: 'À facturer : ', b: money(a.due_ht), post: scope });
    if (a.credit_ht !== 0) out.push({ pre: 'Avoirs prévus : ', b: money(a.credit_ht), post: scope });
    if (!a.complete) out.push({ pre: plural(a.blocking.length || 1, 'blocage', 'blocages'), cls: 'warn-inline', title: a.blocking.join('\n') });
  }
  const next = minDay(...rental.map((l) => l.summary?.next_due_on));
  if (next) out.push({ pre: 'Prochaine échéance : ', b: frWeekday(next) });
  const reasons: string[] = [];
  for (const l of rental) {
    if (!interruptedOn(l, v.as_of)) continue;
    // Motif de la suspension : jamais un geste (motif offert), dont les jours sont OFFERED et non interrompus.
    const i = interruptionsAt(l, v.as_of).find((x) => !names.reasonOffered(x.reason_id)) ?? null;
    const name = i ? names.reasonName(i.reason_id) : 'début de facturation attendu';
    if (!reasons.includes(name)) reasons.push(name);
  }
  if (reasons.length) out.push({ pre: 'Interruptions en cours : ', b: reasons.join(', ') });
  const last = v.invoices.filter((i) => i.invoiced_on && i.doc_type !== 'AVOIR').sort((x, y) => (x.invoiced_on! < y.invoiced_on! ? -1 : x.invoiced_on! > y.invoiced_on! ? 1 : x.id - y.id)).pop();
  if (last) {
    const who = docPatients(last.memos);
    const ref = [last.doc_ref, who.text].filter((x) => !!x).join(' · ');
    out.push({ pre: 'Dernier passage : ', b: frShort(last.invoiced_on), post: ref ? ` (${ref})` : '', title: who.title || undefined });
  }
  out.push({ pre: 'Calculé au ', b: frWeekday(v.as_of) });
  return out;
}

// Dossier, client, FSE (dialogue seulement), puis bénéficiaire et tiers payeur quand ils diffèrent du client.
export function headOf(v: RentalLifecycleFull | null, orderId: number, mode: 'page' | 'dialog', parties: PlanningParties | null): HeadLine[] {
  const out: HeadLine[] = [];
  if (mode === 'dialog') {
    const dossier = `Dossier n° ${v?.order_id ?? orderId}`;
    const fse = v?.is_fse ? ' · FSE' : '';
    if (v?.customer_name && parties?.customerId) out.push({ text: `${dossier} · `, parties: [{ id: parties.customerId, name: v.customer_name }], post: fse });
    else out.push({ text: dossier + (v?.customer_name ? ` · ${v.customer_name}` : '') + fse, parties: [] });
  }
  if (parties?.beneficiary) out.push({ text: 'Bénéficiaire : ', parties: [parties.beneficiary] });
  if (parties?.payers.length) out.push({ text: `${parties.payers.length > 1 ? 'Tiers payeurs' : 'Tiers payeur'} : `, parties: parties.payers });
  return out;
}

// Texte d'une ligne d'en-tête, noms compris (tests, infobulles).
export function headText(h: HeadLine): string {
  return h.text + h.parties.map((p) => p.name).join(', ') + (h.post ?? '');
}

// data.recipient (bénéficiaire distinct, comme la F3) et payer_id des créances différents du client.
export function partyIdsOf(order: any): { customer: number | null; recipient: number | null; payers: number[] } {
  const customer = Number(order?.customer) || 0;
  const r = Number(order?.data?.recipient) || 0;
  const payers = [...new Set<number>((order?.lnk_receivables ?? []).map((x: any) => Number(x?.payer_id) || 0))].filter((id) => id > 0 && id !== customer);
  return { customer: customer > 0 ? customer : null, recipient: r > 0 && r !== customer ? r : null, payers };
}

export function partnerLabel(p: any, id: number): string {
  const x = Array.isArray(p) ? p[0] : p;
  return `${x?.firstname ?? ''} ${x?.name ?? ''}`.trim() || `tiers ${id}`;
}

// « Nom prénom », comme customer_name de la vue et la carte du bénéficiaire : le patient des lignes sans mémo.
export function partnerLastFirst(p: any): string {
  const x = Array.isArray(p) ? p[0] : p;
  return `${x?.name ?? ''} ${x?.firstname ?? ''}`.trim();
}

// end = dernier jour non facturé, inclus.
function interruptionText(i: RentalInterruption, names: PlanningNames): string {
  const startTxt = i.start ? `du ${frShort(i.start)}` : 'dès la livraison';
  const endTxt = i.end ? `au ${frShort(i.end)} inclus` : '(ouverte)';
  return `${names.reasonName(i.reason_id)} · ${startTxt} ${endTxt} · saisie par ${names.userName(i.created.id_user)} le ${dayOfInstant(i.created.dat)}`;
}

export function pieceText(e: RentalLedgerEntry): string {
  return `${docTypeLabel(e.doc_type)} ${e.doc_ref ?? 'n° ' + e.invoice_id}${e.invoiced_on ? ' du ' + frShort(e.invoiced_on) : ''} : ${money(e.net_taxx)} HT`;
}

// Infobulle d'un jour d'une rangée de matériel, une ligne par élément présent (E.3).
export function dayTip(l: RentalLineView, day: Day, v: RentalLifecycleFull, names: PlanningNames): string[] {
  const name = l.product_name ?? `Article ${l.product}`;
  const out = [`${frWeekday(day)} · ${name}`];
  if (l.lifecycle_unreadable || l.decisions == null) {
    out.push(l.anomalies?.[0] ?? 'Cycle de facturation illisible');
    return out;
  }
  const state = shownStateAt(l, day, v.as_of);
  const kind = state === 'INTERRUPTED' ? interruptedKindAt(l, day) : state && CREDITABLE_STATES.has(state) ? creditKindAt(l, day) : null;
  const p = presenceRange(l);
  const present = !!p && day >= p.start && (p.end === null || day <= p.end);
  if (kind && state !== 'INTERRUPTED') out.push(CREDIT_KIND_LABELS[kind as CreditKind]);
  else if (state === 'OUTSIDE' && present) out.push(OUTSIDE_PRESENT_LABEL);
  else if (state) out.push((kind ? INTERRUPTED_KIND_LABELS[kind] : DAY_STATE_LABELS[state]) + (kind && day >= v.as_of ? ', à venir' : ''));
  const rank = rankSpanAt(l, day);
  if (rank) out.push(`Période ${rank.rank} : du ${frShort(rank.start)} au ${frShort(rank.end)}`);
  for (const e of ledgerAt(l, day)) out.push(pieceText(e));
  if (kind === 'carried') {
    const comp = (l.days ?? []).filter((r) => r.state === 'COMPENSATED');
    if (comp.length) out.push('Rendu par report, payé à nouveau ' + comp.map((r) => `du ${frShort(r.start)} au ${frShort(r.end)}`).join(', '));
  }
  for (const e of kind === 'credited' ? creditPiecesAt(l, day) : []) out.push(`Clic : ouvrir l'avoir ${e.doc_ref ?? 'n° ' + e.invoice_id}`);
  if (state === 'COMPENSATED') {
    out.push('Payé par report d\'un rang déjà facturé');
    for (const c of carriedNotesOf(l)) out.push(`${plural(c.dayCount, 'jour reporté', 'jours reportés')} depuis ${c.docRef ?? 'une pièce antérieure'}`);
  }
  const due = dueAt(l, day);
  if (due) out.push(`À facturer au prochain passage : ${money(due.net_taxx)} HT`);
  const credit = creditAt(l, day);
  if (credit) out.push(credit.error ?? `Avoir au prochain passage sur ${credit.corrects_doc_ref ?? 'n° ' + credit.corrects_invoice_id} : ${money(credit.net_taxx)} HT`);
  // Interruption au motif offert = geste commercial : même ligne « Offert » qu'avant le 27/10/2026.
  for (const i of interruptionsAt(l, day)) out.push(names.reasonOffered(i.reason_id) ? `Offert : ${names.reasonName(i.reason_id)}` : interruptionText(i, names));
  for (const rx of l.decisions?.prescriptions ?? []) {
    if (rx.cancelled || day < rx.start || day > rx.end) continue;
    out.push(`Prescription du ${frShort(rx.start)} au ${frShort(rx.end)}` + (rx.reference ? ` · ${rx.reference}` : ''));
  }
  const next = l.summary?.next_due_on;
  if (state === 'RUNNING' && next) out.push(`Facturable le ${frWeekday(next)}`);
  if (l.realdatdel) out.push(`Livré le ${frShort(deliveredDay(l))}`);
  if (l.return_realdatdel) out.push(`Repris le ${frShort(returnedDay(l))}`);
  if (!l.realdatdel && l.planned_on) out.push(`Livraison prévue le ${frShort(l.planned_on)}`);
  if (!l.return_realdatdel && l.planned_return_on) out.push(`Reprise prévue le ${frShort(l.planned_return_on)}`);
  return out;
}

export function missingTip(d: GroupedDecision, lines: RentalLineView[]): string[] {
  return d.missing.map((id) => lines.find((l) => l.id === id)).filter((l): l is RentalLineView => !!l)
    .map((l) => `« ${l.product_name ?? 'Article ' + l.product} » livré le ${frShort(deliveredDay(l))} pendant cette interruption n'y figure pas`);
}

// Infobulle d'un ruban : motif, matériels, dates, commentaire, auteur, historique des remplacements, annulation.
// Geste ou interruption : « Du … au … inclus », jamais le lendemain du end (règle du 27/10/2026).
export function ribbonTip(d: GroupedDecision, lines: RentalLineView[], names: PlanningNames): string[] {
  const s = d.sample;
  const out = [names.reasonName(s.reason_id)];
  const ids = new Set(d.entries.map((e) => e.lineId));
  out.push(lines.filter((l) => ids.has(l.id)).sort((a, b) => a.seq - b.seq).map((l) => l.product_name ?? `Article ${l.product}`).join(', '));
  out.push((s.start ? `Du ${frWeekday(s.start)}` : 'Dès la livraison') + (s.end ? ` au ${frWeekday(s.end)} inclus` : ' · dernier jour inconnu'));
  if (s.comment?.trim()) out.push(s.comment.trim());
  out.push(`saisie par ${names.userName(s.created.id_user)} le ${dayOfInstant(s.created.dat)}`);
  const history = new Set<string>();
  for (const h of d.history) {
    const prev = lines.find((l) => l.id === h.lineId)?.decisions?.interruptions.find((i) => i.id === h.id);
    if (!prev) continue;
    const startTxt = prev.start ? `du ${frShort(prev.start)}` : 'dès la livraison';
    const endTxt = prev.end ? `au ${frShort(prev.end)}` : '(ouverte)';
    const c = prev.cancelled;
    history.add(`Modifiée : remplace l'entrée ${startTxt} ${endTxt}` + (c ? ` (annulée le ${dayOfInstant(c.dat)} par ${names.userName(c.id_user)} : ${c.reason})` : ''));
  }
  out.push(...history);
  const c = s.cancelled;
  if (c) out.push(`Annulée le ${dayOfInstant(c.dat)} par ${names.userName(c.id_user)} : ${c.reason}`);
  if (!d.cancelled) out.push(...missingTip(d, lines));
  out.push(!d.cancelled ? 'Clic : modifier' : 'Clic : détail');
  return out;
}
